/** @jsxImportSource react */
import { Box, InputAdornment, MenuItem, Stack, TextField } from '@mui/material'
import FieldInput from '../records/FieldInput'
import PersonInput from '../records/PersonInput'
import InsertFieldMenu from './InsertFieldMenu'
import { TODAY_TOKEN, isText, todayOffset, todayToken, tokenOptions } from './automationModel'

const FIXED = '__fixed__'

/**
 * A value for `field`: either a fixed value (the field's normal input) or a dynamic one
 * ("Today + 3 days", "Person who made the change", "Copy Owner"...). Text fields can also hold
 * {{placeholders}}. `ctx` = { entityType, sourceFields } of the triggering record.
 */
const ValueEditor = ({ field, value, onChange, ctx, label = 'Value', allowTemplates = false }) => {
  const tokens = tokenOptions(field, ctx)
  const mode = typeof value === 'string' && TODAY_TOKEN.test(value) ? '$today'
    : tokens.some((t) => t.value === value) ? value : FIXED
  // the field's own rules (required, read-only) don't apply to a rule's settings
  const plain = { ...field, label, required: false, readOnly: false, helpText: null, placeholder: null }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      {tokens.length > 0 && (
        <TextField select label="Set to" value={mode} sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
          onChange={(e) => onChange(e.target.value === FIXED ? null : e.target.value)}>
          <MenuItem value={FIXED}>A fixed value</MenuItem>
          {tokens.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
      )}
      {mode === '$today' && (
        <TextField label="Days from today" type="number" value={todayOffset(value)} sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
          onChange={(e) => onChange(todayToken(Math.trunc(Number(e.target.value) || 0)))}
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          helperText="Negative for days ago" />
      )}
      {mode === FIXED && (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, '& > .MuiFormControl-root': { width: '100%' } }}>
          {field.fieldType === 'USER'
            ? <PersonInput value={value || ''} onChange={(v) => onChange(v || null)} label={label} allowMe={false} />
            : <FieldInput field={plain} value={value ?? (field.fieldType === 'MULTI_SELECT' ? [] : '')} onChange={(v) => onChange(v === '' ? null : v)} />}
          {allowTemplates && isText(field) && (
            <Box><InsertFieldMenu fields={ctx.sourceFields} onInsert={(p) => onChange(`${value || ''}${p}`)} /></Box>
          )}
        </Box>
      )}
    </Stack>
  )
}

export default ValueEditor
